import {memo, useCallback, useMemo, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import {
  imageUrlAdapterImageTypes,
  imageUrlAdapterTransformTypes,
  useImageUrlAdapter
} from '@adv-mt/mt-hook-use-image-url-adapter'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {
  BASE_CLASS,
  IMAGE_WIDTH_DESKTOP,
  IMAGE_WIDTH_MOBILE,
  MEDIA_TYPES,
  MODAL_IMAGE_WIDTH_DESKTOP,
  MODAL_IMAGE_WIDTH_MOBILE,
  VIDEO_POSITIONS
} from './config.js'
import DesktopGallery from './DesktopGallery.js'
import GalleryModal from './GalleryModal.js'
import MobileGallery from './MobileGallery.js'
import useGalleryUrlParam from './useGalleryUrlParam.js'

const buildMediaItems = ({photos = [], video}) => {
  const imageItems = photos.map(src => ({type: MEDIA_TYPES.IMAGE, src}))

  if (!video?.url) return imageItems

  const videoItem = {
    type: MEDIA_TYPES.VIDEO,
    src: video.url,
    provider: video.provider,
    autoPlay: video.autoPlay,
    poster: video.poster
  }
  return video.position === VIDEO_POSITIONS.FIRST ? [videoItem, ...imageItems] : [...imageItems, videoItem]
}

const GalleryPanel = ({
  ad,
  adType,
  video,
  breadcrumb,
  contractId,
  extendedTrackingData,
  hasDiscardButton,
  isFavorite,
  isProfessionalDealer,
  phone,
  hidePhone,
  seller,
  showGalleryAd
}) => {
  const {domain, i18n} = useSuiContext()
  const {track: analyticsTrack} = useAnalyticsTrack()
  const config = domain.get('config')
  const isMobile = config.get('isMobile')
  const isDesktop = useMediaQuery(useMediaQueryBreakpoints.sm, {defaultMatches: !isMobile})

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalStartIndex, setModalStartIndex] = useState(0)
  const [shouldAutoPlayVideo, setShouldAutoPlayVideo] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)

  useGalleryUrlParam(isModalOpen && showGalleryAd && isDesktop)

  const mediaItems = useMemo(() => buildMediaItems({photos: ad?.photos ?? [], video}), [ad?.photos, video])

  const getImagesAdapted = useImageUrlAdapter()
  const {images: adaptedMediaItems} = getImagesAdapted({
    images: mediaItems,
    options: {
      imageWidth: isDesktop ? IMAGE_WIDTH_DESKTOP : IMAGE_WIDTH_MOBILE,
      imageType: imageUrlAdapterImageTypes.GALLERY,
      transformType: imageUrlAdapterTransformTypes.DEVICE_CROP
    }
  })
  const {images: modalAdaptedMediaItems} = getImagesAdapted({
    images: mediaItems,
    options: {
      imageWidth: isDesktop ? MODAL_IMAGE_WIDTH_DESKTOP : MODAL_IMAGE_WIDTH_MOBILE,
      imageType: imageUrlAdapterImageTypes.GALLERY,
      transformType: imageUrlAdapterTransformTypes.DEVICE_CROP
    }
  })

  const hasVideo = Boolean(video?.url)
  const videoIndex = hasVideo ? mediaItems.findIndex(item => item.type === MEDIA_TYPES.VIDEO) : -1
  const galleryLabel = i18n.t('COMMON.GALLERY.COUNT', {count: mediaItems.length})
  const showControls = !isVideoPlaying
  const canPlayVideoInline = !isDesktop || video?.position === VIDEO_POSITIONS.FIRST

  const trackDetailGalleryClicked = useCallback(
    async (index, source) => {
      const {
        TRACKING_EVENT_NAMES: {DETAIL_GALLERY_CLICKED},
        HIT_ORIGINS,
        HIT_INFORMATIONS
      } = config.get('tracking')
      const eventData = await domain.get('get_event_tracking_data_use_case').execute({
        ad,
        event: DETAIL_GALLERY_CLICKED,
        origin: HIT_ORIGINS.AD_DETAIL,
        hitInformation:
          mediaItems[index]?.type === MEDIA_TYPES.VIDEO
            ? HIT_INFORMATIONS.GALLERY_DETAIL_VIDEO
            : HIT_INFORMATIONS.GALLERY_DETAIL_IMAGE,
        hitInformation2: source
      })
      analyticsTrack(eventData)
    },
    [ad, analyticsTrack, config, domain, mediaItems]
  )

  const trackVideoButtonClick = useCallback(async () => {
    const {
      TRACKING_EVENT_NAMES: {AD_VIDEO_BUTTON_CLICKED},
      HIT_ORIGINS
    } = config.get('tracking')
    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      ad,
      event: AD_VIDEO_BUTTON_CLICKED,
      origin: HIT_ORIGINS.AD_DETAIL
    })
    analyticsTrack(eventData)
  }, [ad, analyticsTrack, config, domain])

  const trackVideoPlay = useCallback(async () => {
    const {
      TRACKING_EVENT_NAMES: {AD_VIDEO_PLAYED},
      HIT_ORIGINS
    } = config.get('tracking')
    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      ad,
      event: AD_VIDEO_PLAYED,
      origin: HIT_ORIGINS.AD_DETAIL
    })
    analyticsTrack(eventData)
  }, [ad, analyticsTrack, config, domain])

  const handleOpenGallery = useCallback(
    (index, {play = false, isButton = false} = {}) => {
      const {HIT_INFORMATIONS} = config.get('tracking')
      const hitInformation2 =
        play || isButton ? HIT_INFORMATIONS.GALLERY_DETAIL_BUTTON : HIT_INFORMATIONS.GALLERY_DETAIL_IMAGE
      if (play) trackVideoButtonClick()
      trackDetailGalleryClicked(index ?? 0, hitInformation2)
      setModalStartIndex(index ?? 0)
      setShouldAutoPlayVideo(play)
      setIsModalOpen(true)
    },
    [trackVideoButtonClick, trackDetailGalleryClicked, config]
  )

  const handleVideoPlayingChange = useCallback(
    isPlaying => {
      setIsVideoPlaying(isPlaying)
      if (isPlaying) trackVideoPlay()
    },
    [trackVideoPlay]
  )

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setIsVideoPlaying(false)
  }, [])

  if (mediaItems.length === 0) return null

  const GalleryVariant = isDesktop ? DesktopGallery : MobileGallery

  return (
    <>
      <section className={cx(BASE_CLASS, `${BASE_CLASS}--${isDesktop ? 'desktop' : 'mobile'}`)}>
        <GalleryVariant
          mediaItems={adaptedMediaItems}
          alt={ad.title}
          galleryLabel={galleryLabel}
          hasVideo={hasVideo}
          showControls={showControls}
          videoIndex={videoIndex}
          canPlayInline={canPlayVideoInline}
          isModalOpen={isModalOpen}
          onOpenGallery={handleOpenGallery}
          onVideoButtonClick={trackVideoButtonClick}
          onVideoPlayingChange={handleVideoPlayingChange}
        />
      </section>

      <GalleryModal
        isOpen={isModalOpen}
        startIndex={modalStartIndex}
        mediaItems={modalAdaptedMediaItems}
        alt={ad.title}
        hasVideo={hasVideo}
        videoIndex={videoIndex}
        shouldAutoPlayVideo={shouldAutoPlayVideo}
        ad={ad}
        adType={adType}
        breadcrumb={breadcrumb}
        contractId={contractId}
        extendedTrackingData={extendedTrackingData}
        hasDiscardButton={hasDiscardButton}
        hidePhone={hidePhone}
        isFavorite={isFavorite}
        isProfessionalDealer={isProfessionalDealer}
        phone={phone}
        seller={seller}
        showGalleryAd={showGalleryAd && isDesktop}
        onClose={handleCloseModal}
        onVideoPlayingChange={handleVideoPlayingChange}
      />
    </>
  )
}

GalleryPanel.displayName = 'GalleryPanel'

GalleryPanel.propTypes = {
  ad: PropTypes.shape({
    title: PropTypes.string,
    photos: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  adType: PropTypes.object,
  video: PropTypes.shape({
    url: PropTypes.string,
    provider: PropTypes.string,
    position: PropTypes.oneOf(Object.values(VIDEO_POSITIONS)),
    autoPlay: PropTypes.bool
  }),
  breadcrumb: PropTypes.arrayOf(PropTypes.shape({url: PropTypes.string})),
  contractId: PropTypes.string,
  extendedTrackingData: PropTypes.object,
  hasDiscardButton: PropTypes.bool,
  isFavorite: PropTypes.bool,
  hidePhone: PropTypes.bool,
  isProfessionalDealer: PropTypes.bool,
  phone: PropTypes.string,
  seller: PropTypes.object,
  showGalleryAd: PropTypes.bool
}

export default memo(GalleryPanel)
